import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom-v5-compat';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { useLuxonUserDateFormat } from '../../../../../hooks/use-datetime-formats';
import { useModal } from '../../../../../hooks/use-modal';
import { BASE_PRODUCTS_PATH, TRANSFER_ORDERS_PATH } from '../../../../../routes/constants';
import { mapTransferOrderToFormData } from '../../api/transfer-order-form-mapper';
import { getTransferOrder } from '../../api/transfer-order-api';
import { transferOrderStatuses } from '../../data/transfer-order-statuses';
import { messages } from '../messages';

import type { CreateTransferOrderFormData } from '../../components/create-transfer-order-drawer/types';

const TransferOrdersBaseRoute = `${BASE_PRODUCTS_PATH}${TRANSFER_ORDERS_PATH}`;

type UseDraftTransferOrderDrawerResult = {
  isLoadingDraft: boolean;
  draftInitialFormData: CreateTransferOrderFormData | null;
  draftTransferOrderId: number | undefined;
  isDrawerOpen: boolean;
  closeDrawer: () => void;
  onClick: () => Promise<void>;
};

export const useDraftTransferOrderDrawer = (transferOrderId: number, status: string): UseDraftTransferOrderDrawerResult => {
  const navigate = useNavigate();
  const [isLoadingDraft, setIsLoadingDraft] = useState(false);
  const [draftInitialFormData, setDraftInitialFormData] = useState<CreateTransferOrderFormData | null>(null);
  const [draftTransferOrderId, setDraftTransferOrderId] = useState<number | undefined>(undefined);
  const { open: openDrawer, close: closeDrawer, isOpen: isDrawerOpen } = useModal();
  const luxonDateFormat = useLuxonUserDateFormat();

  const onClick = useCallback(async () => {
    if (status === transferOrderStatuses.draft) {
      setIsLoadingDraft(true);
      try {
        const transferOrder = await getTransferOrder(transferOrderId).promise;
        if (transferOrder) {
          setDraftInitialFormData(mapTransferOrderToFormData(transferOrder, luxonDateFormat));
          setDraftTransferOrderId(transferOrderId);
          openDrawer();
        }
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.loadDraftErrorTitle} />,
          content: <FormattedMessage {...messages.loadDraftErrorContent} />,
        });
      } finally {
        setIsLoadingDraft(false);
      }
      return;
    }
    navigate(`${TransferOrdersBaseRoute}/${transferOrderId}`);
  }, [luxonDateFormat, navigate, openDrawer, status, transferOrderId]);

  return {
    isLoadingDraft,
    draftInitialFormData,
    draftTransferOrderId,
    isDrawerOpen,
    closeDrawer,
    onClick,
  };
};
